"use client";

import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@cantera/design-system";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { errorDigest } from "@/lib/error-reporting";
import { confirmPendingReferral, declinePendingReferral, dismissPendingReferral, getPendingReferral, type PendingReferral } from "./referred-actions";

/**
 * Confirmation modal shown over the sign-up page when a visitor arrived
 * through a referral link. Mounted unconditionally: it fetches its own
 * pending referral after mount and renders nothing when there isn't one.
 */
export function ReferredConfirm() {
  const [pending, setPending] = useState<PendingReferral | null>(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getPendingReferral().then(result => {
      if (cancelled || !result) return;
      setPending(result);
      setOpen(true);
      posthog.capture("referral_confirm_shown", {
        slug: result.slug
      });
    }).catch(err => {
      if (!cancelled) posthog.captureException(err, errorDigest(err));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  if (!pending) return null;
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const {
        success,
        slug
      } = await confirmPendingReferral();
      if (success) {
        // Report the slug the server credited, which a second link click may
        // have changed.
        posthog.capture("referral_confirm_answered", {
          slug: slug ?? pending.slug,
          outcome: "accepted"
        });
      } else {
        posthog.capture("referral_confirm_answered", {
          slug: pending.slug,
          outcome: "stale"
        });
      }
      setOpen(false);
    } catch (err_0) {
      posthog.captureException(err_0, errorDigest(err_0));
    } finally {
      setSubmitting(false);
    }
  };
  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await declinePendingReferral();
      posthog.capture("referral_confirm_answered", {
        slug: pending.slug,
        outcome: "declined"
      });
    } catch (err_1) {
      posthog.captureException(err_1, errorDigest(err_1));
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };
  return <Dialog open={open} onOpenChange={next => {
    // Ignore close attempts while a submission is in flight.
    if (submitting) return;
    if (next) return;
    setOpen(false);
    posthog.capture("referral_confirm_answered", {
      slug: pending.slug,
      outcome: "dismissed"
    });
    void dismissPendingReferral().catch(err_2 => {
      posthog.captureException(err_2, errorDigest(err_2));
    });
  }}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader type="close-only" />

        <div className="space-y-4 px-6 pb-4 text-center">
          <DialogTitle className="heading-lg text-foreground">
            {pending.referrerName} thought you'd be a great fit
          </DialogTitle>

          <p className="body-md text-muted-foreground">
            Do you two know each other? Confirm if you do. If you don't, you can
            decline and still continue.
          </p>
        </div>

        <DialogFooter className="justify-center">
          <Button variant="outline" size="lg" disabled={submitting} onClick={handleDecline}>
            Decline
          </Button>
          <Button size="lg" disabled={submitting} onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}